import React from 'react';
import PropTypes from 'prop-types';
import Spacer from 'common/components/Spacer';
import Pin from '@splashthat/doppler/icons/Pin';
import { formatDateTime } from './utils';

import SessionSpeakers from './SessionSpeakers';

import {
    Card,
    SessionsTag,
    SessionsTagContainer,
    SessionCardHeader,
    SessionText,
    SessionLocation,
    SessionTitleText,
    SessionLabelContainer,
} from './SessionCard.styles';

const BASE_CLASS_NAME = 'SessionCard';

export default function SessionCard({
    cardStyles,
    titleStyles,
    dateStyles,
    speakerNameStyles,
    speakerTitleCompanyStyles,
    descriptionStyles,
    tagStyles,
    labelComponent,
    session,
    colors,
    activeFonts,
    onClickSpeakerAvatar,
    footerComponent,
}) {
    return (
        <Card
            $borderWidth={cardStyles.borderWidth}
            $borderRadius={cardStyles.borderRadius}
            $backgroundColor={colors[cardStyles.backgroundColor]}
            $borderColor={colors[cardStyles.borderColor]}
            data-qa={`${BASE_CLASS_NAME}__card-container`}
        >
            <SessionCardHeader>
                <SessionTitleText
                    $fontSize={titleStyles.fontSize}
                    $fontColor={colors[titleStyles.fontColor]}
                    $fontFamily={activeFonts[titleStyles.fontFamily]}
                    data-qa={`${BASE_CLASS_NAME}__title`}
                >
                    {session.name}
                </SessionTitleText>
                {labelComponent && <SessionLabelContainer>{labelComponent}</SessionLabelContainer>}
            </SessionCardHeader>

            <Spacer $spacing="1rem" />

            <SessionText
                $fontSize={dateStyles.fontSize}
                $fontColor={colors[dateStyles.fontColor]}
                $fontFamily={activeFonts[dateStyles.fontFamily]}
                data-qa={`${BASE_CLASS_NAME}__date`}
            >
                {formatDateTime(session.start, session.end)}
            </SessionText>

            {session.location ? (
                <>
                    <Spacer $spacing="1rem" />
                    <SessionLocation
                        $fontSize={dateStyles.fontSize}
                        $fontColor={colors[dateStyles.fontColor]}
                        $fontFamily={activeFonts[dateStyles.fontFamily]}
                        data-qa={`${BASE_CLASS_NAME}__date`}
                    >
                        <Pin aria-hidden="true" focusable="false" /> {session.location}
                    </SessionLocation>
                </>
            ) : null}

            <Spacer $spacing="1.25rem" />

            {!!session.speakers.length && (
                <SessionSpeakers
                    speakers={session.speakers}
                    colors={colors}
                    activeFonts={activeFonts}
                    speakerNameStyles={speakerNameStyles}
                    speakerTitleCompanyStyles={speakerTitleCompanyStyles}
                    onClickSpeakerAvatar={onClickSpeakerAvatar}
                />
            )}

            <SessionText
                $fontSize={descriptionStyles.fontSize}
                $fontColor={colors[descriptionStyles.fontColor]}
                $fontFamily={activeFonts[descriptionStyles.fontFamily]}
                data-qa={`${BASE_CLASS_NAME}__description`}
            >
                {session.description}
            </SessionText>

            <Spacer $spacing="1.5rem" />

            <SessionsTagContainer>
                {session.tags.map((name, index) => (
                    <SessionsTag
                        key={session.id + index}
                        $fontSize={tagStyles.fontSize}
                        $backgroundColor={colors[tagStyles.backgroundColor]}
                        $borderColor={colors[tagStyles.borderColor]}
                        $fontColor={colors[tagStyles.fontColor]}
                        $fontFamily={activeFonts[tagStyles.fontFamily]}
                        $borderWidth={tagStyles.borderWidth}
                        $borderRadius={tagStyles.borderRadius}
                        data-qa={`${BASE_CLASS_NAME}__tag_${index}`}
                    >
                        {name}
                    </SessionsTag>
                ))}
            </SessionsTagContainer>

            {footerComponent}
        </Card>
    );
}

SessionCard.propTypes = {
    cardStyles: PropTypes.shape({
        backgroundColor: PropTypes.number,
        borderColor: PropTypes.number,
        borderWidth: PropTypes.object,
        borderRadius: PropTypes.number,
    }).isRequired,
    titleStyles: PropTypes.shape({
        fontColor: PropTypes.number,
        fontSize: PropTypes.number,
        fontFamily: PropTypes.number,
    }).isRequired,
    dateStyles: PropTypes.shape({
        fontColor: PropTypes.number,
        fontSize: PropTypes.number,
        fontFamily: PropTypes.number,
    }).isRequired,
    speakerNameStyles: PropTypes.shape({
        fontColor: PropTypes.number,
        fontSize: PropTypes.number,
        fontFamily: PropTypes.number,
    }).isRequired,
    speakerTitleCompanyStyles: PropTypes.shape({
        fontColor: PropTypes.number,
        fontSize: PropTypes.number,
        fontFamily: PropTypes.number,
    }).isRequired,
    descriptionStyles: PropTypes.shape({
        fontColor: PropTypes.number,
        fontSize: PropTypes.number,
        fontFamily: PropTypes.number,
    }).isRequired,
    tagStyles: PropTypes.shape({
        fontColor: PropTypes.number,
        fontSize: PropTypes.number,
        fontFamily: PropTypes.number,
        backgroundColor: PropTypes.number,
        borderColor: PropTypes.number,
        borderWidth: PropTypes.object,
        borderRadius: PropTypes.number,
    }).isRequired,
    labelComponent: PropTypes.node,
    session: PropTypes.shape({
        name: PropTypes.string,
        start: PropTypes.string,
        end: PropTypes.string,
        speakers: PropTypes.array,
        description: PropTypes.string,
        location: PropTypes.string,
        tags: PropTypes.array,
        id: PropTypes.number,
    }).isRequired,
    colors: PropTypes.array.isRequired,
    activeFonts: PropTypes.array.isRequired,
    footerComponent: PropTypes.node,
    onClickSpeakerAvatar: PropTypes.func.isRequired,
};

SessionCard.defaultProps = {
    footerComponent: null,
    labelComponent: null,
};
